import {FC, useEffect} from 'react'

import cn from 'classnames/bind'
import {useRouter} from 'next/router'

import BrainableBlock from 'components/brainable/BrainableBlock'
import BrainableLayout from 'components/brainable/BrainableLayout'
import CheckoutForm from 'components/brainable/CheckoutForm'
import PaymentFeatures from 'components/brainable/PaymentFeatures'
import SecureBlock from 'components/brainable/SecureBlock'
import {Col, Container, Row} from 'components/elements/grid'
import Spinner from 'components/elements/Spinner'
import Template404 from 'components/templates/Template404'
import paymentPlansConfig from 'src/configs/paymentPlans.config'
import {TPageComponent} from 'src/types'
import {posthogTrack, capitalize, exhaustiveCheck, klaviyoTrack} from 'src/utils'
import {useAuthStore} from 'src/utils/contexts/RootStore.context'

import s from './TemplateBrainablePayment.module.scss'

const cx = cn.bind(s)

interface ITemplateBrainablePaymentProps {}

export const getBillingDescriptionByPlan = (planConfig: IPaymentPlanConfig) => {
	const planPayButtonText = planConfig.subscription.frequency !== 'once' ? 'Subscribe' : 'Purchase'
	switch (planConfig.key) {
		case 'annual':
		case 'monthly':
		case 'monthly-a':
		case 'monthly-b':
			return `By clicking "${planPayButtonText}" you are providing your
            electronic signature authorizing Paystub.org to charge your
            card as described. You also agree that you will be billed $${planConfig.subscription.price} 
            ${planConfig.subscription.frequency}
            until you cancel your account, which can be done
            quickly and easily by contacting our support team at 855-607-1274 / support@paystub.org`
		// case '**with-trial**':
		// 	return `Your $${planConfig.trial?.price} package lasts ${planConfig.trial?.duration} days.
		//         By clicking "${planPayButtonText}" you are providing your
		//         electronic signature authorizing Paystub.org to charge your
		//         card as described. You also agree that unless you cancel your account during your ${planConfig.trial?.duration} day try out period
		//         you will be billed $${planConfig.subscription.price}
		//         ${planConfig.subscription.frequency}
		//         until you cancel your account, which can be done
		//         quickly and easily by contacting our support team at 855-607-1274 / support@paystub.org`
		case 'single':
			return `By clicking "${planPayButtonText}" you are providing your
            electronic signature authorizing Paystub.org to charge your
            card as described.`
		default:
			const _exhaustiveCheck: never = planConfig.key
			exhaustiveCheck(_exhaustiveCheck)
	}
}

const TemplateBrainablePayment: TPageComponent<ITemplateBrainablePaymentProps> = () => {
	const router = useRouter()
	const authStore = useAuthStore()

	const planKey = router.query.plan as keyof typeof paymentPlansConfig
	const planConfig = paymentPlansConfig[planKey]

	useEffect(() => {
		posthogTrack('[Payment] Page opened')
		klaviyoTrack('Payment opened', {
			paymentType: planKey,
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (!authStore.hasActiveSubscription) {
			posthogTrack(`[PAYMENT unsubscribed] page opened`)
		}
	}, [authStore.hasActiveSubscription])

	if (planKey && planConfig === undefined) {
		return <Template404 />
	}

	return planConfig ? (
		<BrainableLayout>
			<div className={cx('payment__wrapper', 'bgBlueHalf')}>
				<Container className={s.container} fluid={true}>
					<Row className={cx('row')}>
						<Col className={cx('col')}>
							<BrainableBlock title="Payment Information" dense>
								<div className={s.payment__content}>
									<Row className={cx('row')}>
										<Col md={5} className={cx('col', 'summary__col')}>
											<div className={s.summary}>
												<div className={s.summary__block}>
													<div className={s.summary__title}>Summary</div>
													<BillingInfo description={getBillingDescriptionByPlan(planConfig)} />
												</div>
												<PackageInfo planConfig={planConfig} />
												<AccountInfo
													firstName={authStore.user?.firstName}
													lastName={authStore.user?.lastName}
													email={authStore.user?.email}
												/>
											</div>
										</Col>
										<Col md={7} className={cx('col', 'checkout__col')}>
											<CheckoutForm planConfig={planConfig} />
											<div className={cx('checkout-col-footer')}>
												<PaymentFeatures className={cx('features')} />
												<SecureBlock />
											</div>
										</Col>
									</Row>
								</div>
							</BrainableBlock>
						</Col>
					</Row>
				</Container>
			</div>
		</BrainableLayout>
	) : (
		<Spinner />
	)
}

interface IBillingInfoProps {
	description: string
}

const BillingInfo: FC<IBillingInfoProps> = ({description}) => (
	<div className={s.billing}>
		<p className={s.summary__label}>Billing</p>
		<p className={s.billing__description}>{description}</p>
	</div>
)

interface IPackageInfoProps {
	planConfig: IPaymentPlanConfig
}

const PackageInfo: FC<IPackageInfoProps> = ({planConfig}) => (
	<div className={cx('package', 'summary__block')}>
		<p className={s.summary__label}>Package</p>
		<div className={cx('packages')}>
			<div className={cx('package__item')}>
				<div className={cx('package__col')}>
					<p className={cx('package__label')}>{capitalize(planConfig.planLabel)}</p>
				</div>
				<div className={cx('package__col')}>
					<p className={cx('package__label', 'package__label--highlighted')}>
						{'$'}
						{planConfig.trial ? planConfig.trial.price : planConfig.subscription.price} today
					</p>
				</div>
			</div>
			{planConfig.subscription.frequency !== 'once' && (
				<div className={cx('package__item')}>
					<div className={cx('package__col')}>
						<p className={s.package__info}>
							{planConfig.trial ? planConfig.trial.duration : planConfig.subscription.duration} Day
							Access
						</p>
					</div>
					<div className={cx('package__col')}>
						<p className={s.package__info}>
							Then {'$'}
							{planConfig.subscription.price}/{planConfig.subscription.frequency}
						</p>
					</div>
				</div>
			)}
		</div>
	</div>
)

interface IAccountInfoProps {
	firstName?: string
	lastName?: string
	email?: string
}

interface IAccountListItemProps {
	header: string
	value: string
}

const AccountInfo: FC<IAccountInfoProps> = ({firstName, email, lastName}) => {
	const AccountListItem: FC<IAccountListItemProps> = ({header, value}) => (
		<li className={s.account__item}>
			<p className={s.account__header}>{header}</p>
			<p className={s.account__text}>{value}</p>
		</li>
	)
	return (
		<div className={s.account}>
			<div className={s.summary__label}>Account information</div>
			<ul className={s.account__list}>
				{firstName && <AccountListItem header="First name" value={firstName} />}
				{lastName && <AccountListItem header="Last name" value={lastName} />}
				{email && <AccountListItem header="Email address" value={email} />}
			</ul>
		</div>
	)
}

TemplateBrainablePayment.displayName = 'TemplateBrainablePayment'

export default TemplateBrainablePayment
